import React, { useMemo, useCallback, useReducer } from 'react';

import {
  buildAuthorizedDeleteRequest,
  buildAuthorizedGetRequest,
  buildAuthorizedPutRequest,
} from '../../http/request-templates';
import { ImpersonateContext } from './ImpersonateContext';
import impersonateReducer, {
  RCV_GET_IMPERSONATE,
  REQ_GET_IMPERSONATE,
  ERR_GET_IMPERSONATE,
  REQ_SET_IMPERSONATE,
  RCV_SET_IMPERSONATE,
  ERR_SET_IMPERSONATE,
  REQ_REMOVE_IMPERSONATE,
  RCV_REMOVE_IMPERSONATE,
  ERR_REMOVE_IMPERSONATE,
  impersonateReducerInitialState,
} from './impersonateReducer';
import useAuth from '../Auth/useAuth';
import usePermissions from '../Permissions/usePermissions';
import { Impersonate } from './ImpersonateModels';
import useHttp from '../../http/useHttp';

interface ImpersonateProviderProps {
  children: React.ReactNode;
}

const ImpersonateProvider = ({ children }: ImpersonateProviderProps) => {
  const { getAccessToken } = useAuth();
  const { getPermissions } = usePermissions();
  const { httpRawJson } = useHttp();

  const [impersonateState, dispatch] = useReducer(
    impersonateReducer,
    impersonateReducerInitialState
  );

  const getImpersonate = useCallback(async () => {
    try {
      dispatch({ type: REQ_GET_IMPERSONATE });
      const response = await httpRawJson<Impersonate>(
        `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/admin/impersonate`,
        buildAuthorizedGetRequest(await getAccessToken())
      );
      dispatch({ type: RCV_GET_IMPERSONATE, impersonate: response });
    } catch (e) {
      dispatch({ type: ERR_GET_IMPERSONATE });
    }
  }, [getAccessToken, httpRawJson]);

  const setImpersonate = useCallback(
    async (email: string) => {
      try {
        dispatch({ type: REQ_SET_IMPERSONATE, email });
        const response = await httpRawJson<Impersonate>(
          `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/admin/impersonate?email=${encodeURIComponent(
            email
          )}`,
          buildAuthorizedPutRequest(await getAccessToken())
        );
        getPermissions();
        dispatch({ type: RCV_SET_IMPERSONATE, impersonate: response });
      } catch (e) {
        dispatch({ type: ERR_SET_IMPERSONATE });
      }
    },
    [getAccessToken, getPermissions, httpRawJson]
  );

  const removeImpersonate = useCallback(async () => {
    try {
      dispatch({ type: REQ_REMOVE_IMPERSONATE });
      await httpRawJson(
        `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/admin/impersonate`,
        buildAuthorizedDeleteRequest(await getAccessToken())
      );
      getPermissions();
      dispatch({ type: RCV_REMOVE_IMPERSONATE });
    } catch (e) {
      dispatch({ type: ERR_REMOVE_IMPERSONATE });
    }
  }, [getAccessToken, getPermissions, httpRawJson]);

  const value = useMemo(
    () => ({
      ...impersonateState,
      getImpersonate,
      setImpersonate,
      removeImpersonate,
    }),
    [getImpersonate, impersonateState, removeImpersonate, setImpersonate]
  );

  return <ImpersonateContext.Provider value={value}>{children}</ImpersonateContext.Provider>;
};

export default ImpersonateProvider;
